/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useMemo } from 'react';

import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import { isCodeSearchEnabled } from '@atlassian/search-common/code-search-mode';
import { CodeBlock } from '@atlassian/search-common/code/code-block';
import { searchHighlightDecoration } from '@atlassian/search-common/code/search-highlight-decoration';
import { isSupportedLanguage } from '@atlassian/search-common/code/supported-languages';
import type { Decoration } from '@atlassian/search-common/code/types/decoration';
import { highlightMatchedText } from '@atlassian/search-common/highlight-matched-text';

import type { SearchResultProps } from '../../../../../../common/types';
import { DescriptionBody } from '../../../../../../common/ui/description-body';
import { IconFor3P } from '../../../../../../common/ui/icon-for-3p';
import { LastModified } from '../../../../../../common/ui/last-modified';
import { SearchResult } from '../../../../../../common/ui/search-result';
import {
	buildCodeSearchPortalUrl,
	getPathScope,
} from '../../../../../../common/utils/code-search-portal-url';
import type { AdvancedSearchResultInteractedMetadata } from '../../../../../../common/utils/events/advanced-search-result-interacted';
import { scmToProductKey } from '../../../../../../common/utils/scm-to-product-key';
import { enrichExcerpt, processExcerpt } from '../../../../../../common/utils/search-result-utils';
import { getSiteMetadata } from '../../../../../../common/utils/sites';
import { ResultAnalytics } from '../../../../../../controllers/analytics/result-analytics';
import type { ResultDerivedDataProps } from '../../../result-derived-data';
import type { searchGraphCode_AtlassianSearchPage$key } from './__generated__/searchGraphCode_AtlassianSearchPage.graphql';
import { messages } from './messages';
import { CodeSearchPortalMenu } from './portal-menu';
import { truncatePath } from './truncate-path';

/** Chunks rendered inline; the rest are one click away in the Code Search Portal. */
const VISIBLE_CHUNK_COUNT = 1;

const styles = cssMap({
	typeLabelContainer: {
		'&::first-letter': {
			textTransform: 'capitalize',
		},
	},
});

export type SearchGraphCodeProps = SearchResultProps<searchGraphCode_AtlassianSearchPage$key>;

export const SearchGraphCode = ({
	preparedResultAttributes,
	intentDetectionTriggered,
	node,
	position,
	cloudId,
	searchQuery,
	selectedProduct,
	primaryProduct,
	primarySubProduct,
	appMode,
	thirdPartyConfigs,
	mainContentWidth,
	selectedSites,
	productConfigs,
}: SearchGraphCodeProps & ResultDerivedDataProps): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment searchGraphCode_AtlassianSearchPage on SearchResultGraphCode {
				__typename
				id
				title
				url
				path
				description
				language
				lastModifiedDate
				matchedChunks {
					id
					sourceCode
					lineRange {
						start
					}
					matchedRanges
				}
				repository {
					name
					namespace
					url
					scm
				}
			}
		`,
		node,
	);

	const { formatMessage } = useIntl();

	const isM1Enabled = isCodeSearchEnabled();

	// Attributed by host, not by the selected app filter. An scm we don't map leaves the
	// product undefined: generic link icon, no product label, no analytics field.
	const scm = data.repository?.scm;
	const product = scmToProductKey(scm);
	const codeContentType = scm ? `${scm} code` : 'code';

	const metadata = useMemo(
		() =>
			({
				id: data.id,
				containerId: null,
				spaceId: null,
				contentType: codeContentType,
				entityType: codeContentType,
				contentId: data.id,
				product,
				...getSiteMetadata(data.id, cloudId, selectedSites),
			}) satisfies AdvancedSearchResultInteractedMetadata,
		[data.id, codeContentType, product, cloudId, selectedSites],
	);

	const { id, url, title, path, description, language, lastModifiedDate, repository } = data;

	const displayTitle = useMemo(() => (path ? truncatePath(path) : title) ?? '', [path, title]);
	const enrichedTitle = useMemo(() => {
		const { highlightedTitle } = highlightMatchedText(displayTitle, searchQuery.split(' '));
		return enrichExcerpt(highlightedTitle || displayTitle);
	}, [displayTitle, searchQuery]);

	const repositoryName = useMemo(() => {
		if (!repository) {
			return null;
		}
		return repository.namespace ? `${repository.namespace}/${repository.name}` : repository.name;
	}, [repository]);

	const headerMetadataParts = useMemo<Array<[string, React.ReactNode]>>(
		() =>
			(
				[
					['label', <Box xcss={styles.typeLabelContainer}>{formatMessage(messages.code)}</Box>],
					language && ['language', language],
					repositoryName && ['repository-name', repositoryName],
					lastModifiedDate && ['last-modified', <LastModified lastModified={lastModifiedDate} />],
				] as Array<[string, React.ReactNode] | false | undefined | null>
			).filter(Boolean) as Array<[string, React.ReactNode]>,
		[formatMessage, language, repositoryName, lastModifiedDate],
	);

	const enrichedDescription = useMemo(() => {
		if (!description) {
			return undefined;
		}
		const { highlightedTitle } = highlightMatchedText(description, searchQuery.split(' '));
		return processExcerpt(highlightedTitle || description);
	}, [description, searchQuery]);

	// The schema reports the language as a display name ("TypeScript"); the component wants a
	// lowercase identifier, and renders plain text for anything it doesn't recognise.
	const codeLanguage = useMemo(() => {
		const candidate = language?.toLowerCase();
		return candidate && isSupportedLanguage(candidate) ? candidate : undefined;
	}, [language]);

	// Code search renders the most relevant chunk as a real code block (matchedChunks is ordered
	// by relevance); legacy mode, and results without chunks, fall back to the plain-text excerpt.
	const descriptionNode = useMemo(() => {
		const codeChunks = isM1Enabled ? (data.matchedChunks ?? []).slice(0, VISIBLE_CHUNK_COUNT) : [];
		if (codeChunks.length > 0) {
			return (
				<Stack space="space.100">
					{codeChunks.map((chunk) => {
						// sourceCode's first line IS file line lineRange.start, and each matchedRanges
						// entry is [lineNumber, startChar, endChar) carrying its own absolute file
						// line — the same numbering CodeBlock uses for firstLineNumber and for a
						// decoration's lineNumber. One line can carry several ranges, so the entries
						// are neither one-per-line nor index-aligned with matchedLineNumbers, which
						// is only the distinct set of matched lines and so is not selected at all.
						// NOTE: this contradicts the AGG schema description of matchedRanges, which
						// documents index-aligned [startChar, endChar] pairs. The payload is
						// authoritative; see QS-10639.
						const decorations = (chunk.matchedRanges ?? []).reduce<Decoration[]>((acc, range) => {
							const [lineNumber, startChar, endChar] = range ?? [];
							if (lineNumber === undefined || startChar === undefined || endChar === undefined) {
								return acc;
							}
							acc.push(searchHighlightDecoration({ lineNumber, startChar, endChar }));
							return acc;
						}, []);

						return (
							<CodeBlock
								key={chunk.id}
								code={chunk.sourceCode}
								language={codeLanguage}
								// Eager rather than the default lazy: a chunk is a handful of lines at or
								// near the fold, so there is little to defer, and lazy loading leaves the
								// highlighted state racing anything that observes the render (VR
								// screenshots included). The grammar chunk is per-language, so several
								// results of the same language on one page pay for it once.
								highlightStrategy="eager"
								firstLineNumber={chunk.lineRange.start}
								decorations={decorations}
								testId="search-graph-code-block"
							/>
						);
					})}
				</Stack>
			);
		}
		return enrichedDescription ? <DescriptionBody description={enrichedDescription} /> : undefined;
	}, [isM1Enabled, data.matchedChunks, codeLanguage, enrichedDescription]);

	const pathScope = useMemo(() => getPathScope(path), [path]);

	const codeSearchPortalUrl = useMemo(
		() =>
			buildCodeSearchPortalUrl({
				searchQuery,
				path,
				scm,
				repositoryName: repository?.name,
				namespace: repository?.namespace,
			}),
		[searchQuery, path, scm, repository],
	);

	// One menu per result, so the accessible name carries the scope.
	const portalScope = pathScope || repositoryName;
	const titleActionsNode = isM1Enabled ? (
		<CodeSearchPortalMenu
			href={codeSearchPortalUrl}
			triggerLabel={
				portalScope
					? formatMessage(messages.moreActionsLabel, { scope: portalScope })
					: formatMessage(messages.moreActions)
			}
			triggerTooltip={formatMessage(messages.moreActions)}
			openInCodeSearchLabel={formatMessage(messages.openInCodeSearch)}
			testId="search-graph-code-portal-menu"
		/>
	) : undefined;

	return (
		<ResultAnalytics
			preparedResultAttributes={preparedResultAttributes}
			intentDetectionTriggered={intentDetectionTriggered}
			metadata={metadata}
			position={position}
		>
			{({
				onResultClicked,
				onResultContextMenu: onResultCtxMenuClicked,
				onCopyLinkButtonClicked,
				onSummarizeInChatButtonClicked,
				onContextMenuCopyButtonClicked,
				onContextMenuButtonClicked,
			}) => (
				<SearchResult
					productConfigs={productConfigs}
					{...data}
					id={id}
					product={product}
					productSelected={!!selectedProduct}
					primaryProduct={primaryProduct}
					primarySubProduct={primarySubProduct}
					onResultClicked={onResultClicked}
					onResultContextMenu={onResultCtxMenuClicked}
					onCopyLinkButtonClicked={onCopyLinkButtonClicked}
					onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
					onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
					onContextMenuButtonClicked={onContextMenuButtonClicked}
					url={url}
					title={enrichedTitle}
					icon={
						// IconFor3P doesn't surface a code-specific icon yet; subtype is intentionally null
						// so the generic product icon is used.
						<IconFor3P
							productConfigs={productConfigs}
							product={product}
							resourceType="code"
							subtype={null}
							thirdPartyConfigs={thirdPartyConfigs ?? {}}
						/>
					}
					headerMetadataParts={headerMetadataParts}
					description={descriptionNode}
					titleActions={titleActionsNode}
					cloudId={cloudId}
					position={position}
					partial={data}
					appMode={appMode}
					isThirdParty
					mainContentWidth={mainContentWidth}
					selectedSites={selectedSites}
				/>
			)}
		</ResultAnalytics>
	);
};

// `any` matches the sibling renderer convention (see isSearchTrello, isSearchJiraIssue).
// Narrower structural types are rejected by TS for type-predicate targets against Relay key types.
export const isSearchGraphCode = (node?: any): node is searchGraphCode_AtlassianSearchPage$key =>
	node?.__typename === 'SearchResultGraphCode';
